import * as React from 'react';
import { isUiModificationsSupportEnabled, isMultiSelectEnabled } from '@helpcenter/feature-flags';
import { FieldDecoratorAsync } from '@atlassian/jsm-ui-modifications-view-request-create/src/ui/field-decorator';
import type { DecoratedField } from '@atlassian/jsm-ui-modifications-view-request-create/src/ui/field-decorator/enabled-field-decorator/types';
import type { FieldBaseProps } from '../fields/field-base';

interface FormComponentProps<V> extends Partial<FieldBaseProps>, Partial<DecoratedField> {
    defaultValue?: V;
    value?: V;
    onChange?: (...args: unknown[]) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
    id: string;
}

interface State<V> {
    controlledValue?: V;
}

export function withControlledFormComponentValue<V = unknown>(
    mapOnChangeValue: (...args: unknown[]) => V,
    valueTransformer?: (value: V | undefined) => unknown
) {
    type WrappedComponentProps = FormComponentProps<V> & {
        valueOverride?: V;
    };

    return (FormComponent: React.ComponentType<FormComponentProps<V>>) =>
        class ControlledFormComponentWrapper extends React.PureComponent<WrappedComponentProps, State<V>> {
            checkIsValueOverride = () => {
                if (this.props.valueOverride === null || this.props.valueOverride === '' || this.props.valueOverride) {
                    return true;
                }
                return false;
            };

            state: State<V> = {
                controlledValue: this.checkIsValueOverride() ? this.props.valueOverride : this.props.defaultValue,
            };

            static displayName = 'ControlledFormComponentWrapper';

            componentDidUpdate(prevProps: Readonly<WrappedComponentProps>, prevState: Readonly<State<V>>) {
                // Handle valueOverride changes (UI Modifications)
                if (
                    this.checkIsValueOverride() &&
                    this.props.valueOverride !== prevProps.valueOverride &&
                    this.props.valueOverride !== prevState.controlledValue
                ) {
                    this.setState({
                        controlledValue: this.props.valueOverride,
                    });
                }

                // Handle external value changes (native automation rules, form resets, etc.)
                if (
                    isMultiSelectEnabled() &&
                    this.props.value !== prevProps.value &&
                    this.props.value !== prevState.controlledValue &&
                    !this.checkIsValueOverride()
                ) {
                    this.setState({
                        controlledValue: this.props.value,
                    });
                }
            }

            onChange = (...args: unknown[]) => {
                this.setState({ controlledValue: mapOnChangeValue(...args) });
                this.props.onChange?.(...args);
            };

            render() {
                const { defaultValue: _1, value: _2, valueOverride: _3, onChange: _4, ...remainingProps } = this.props;

                if (isUiModificationsSupportEnabled()) {
                    const transformedValue = valueTransformer
                        ? valueTransformer(this.state.controlledValue)
                        : this.state.controlledValue;
                    const componentProps = {
                        ...remainingProps,
                        value: transformedValue,
                        onChange: this.onChange,
                        shouldReplaceValue: this.checkIsValueOverride(),
                    };

                    return <FieldDecoratorAsync Component={FormComponent} componentProps={componentProps} />;
                }

                return (
                    <FormComponent {...remainingProps} value={this.state.controlledValue} onChange={this.onChange} />
                );
            }
        };
}
